import { Link, useParams, Navigate } from "react-router-dom";
import {
    CheckCircle2,
    ChevronRight,
    Clock,
    Award,
    BookOpen,
    Users,
    Target,
    Shield,
    Phone,
    MessageCircle,
    ChevronLeft,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import EnquiryForm from "@/components/EnquiryForm";
import useSEO from "@/hooks/useSEO";
import { COURSE_BY_SLUG, COURSES } from "@/data/courses";
import { SITE, telLink, waLink } from "@/data/site";

export default function CoursePage() {
    const { slug } = useParams();
    const course = COURSE_BY_SLUG[slug];

    useSEO({
        title: course?.metaTitle || "Coaching in Pathankot | Dacron Classes",
        description:
            course?.metaDescription ||
            "Top coaching institute in Pathankot. Book a Free Demo Class today.",
        keywords: course?.keywords,
        canonical: `/${slug}`,
        jsonLd: course
            ? {
                  "@context": "https://schema.org",
                  "@graph": [
                      {
                          "@type": "Course",
                          name: course.title,
                          description: course.metaDescription,
                          provider: {
                              "@type": "EducationalOrganization",
                              name: "Dacron Classes",
                              sameAs: SITE.domain,
                          },
                          courseMode: "Onsite",
                          locationCreated: "Pathankot, Punjab, India",
                      },
                      {
                          "@type": "BreadcrumbList",
                          itemListElement: [
                              {
                                  "@type": "ListItem",
                                  position: 1,
                                  name: "Home",
                                  item: SITE.domain + "/",
                              },
                              {
                                  "@type": "ListItem",
                                  position: 2,
                                  name: course.short,
                                  item: `${SITE.domain}/${slug}`,
                              },
                          ],
                      },
                      {
                          "@type": "FAQPage",
                          mainEntity: course.faqs.map((f) => ({
                              "@type": "Question",
                              name: f.q,
                              acceptedAnswer: { "@type": "Answer", text: f.a },
                          })),
                      },
                  ],
              }
            : undefined,
    });

    if (!course) return <Navigate to="/" replace />;

    const benefitIcons = [Award, Target, Users, BookOpen, Clock, Shield];

    return (
        <main data-testid={`course-page-${slug}`}>
            {/* HERO */}
            <section
                data-testid="course-hero"
                className="relative bg-brand-navy text-white overflow-hidden"
            >
                <div
                    className="absolute inset-0 opacity-25 bg-cover bg-center"
                    style={{ backgroundImage: `url(${course.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/95 to-brand-navy/70" />
                <div className="container-x relative pt-12 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <Link
                            to="/"
                            data-testid="back-to-home"
                            className="inline-flex items-center gap-1.5 text-xs tracking-[0.22em] uppercase text-white/70 hover:text-brand-gold mb-6"
                        >
                            <ChevronLeft className="w-3.5 h-3.5" /> Home
                        </Link>
                        <div className="overline text-brand-gold mb-4">
                            {course.category} · Pathankot
                        </div>
                        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.0] tracking-tight text-balance">
                            {course.h1}
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-white/80 max-w-2xl leading-relaxed">
                            {course.hero}
                        </p>
                        <div className="mt-9 flex flex-col sm:flex-row gap-3">
                            <Button
                                asChild
                                data-testid="course-cta-demo"
                                className="rounded-none h-13 px-7 bg-brand-crimson hover:bg-brand-crimsonHover font-heading font-bold tracking-wide h-12"
                            >
                                <a href="#course-enquiry">Book Free Demo Class</a>
                            </Button>
                            <Button
                                asChild
                                data-testid="course-cta-call"
                                variant="outline"
                                className="rounded-none px-7 border-white text-white hover:bg-white hover:text-brand-navy font-heading font-semibold bg-transparent h-12"
                            >
                                <a href={telLink}>
                                    <Phone className="w-4 h-4 mr-2" /> {SITE.phone}
                                </a>
                            </Button>
                            <Button
                                asChild
                                data-testid="course-cta-whatsapp"
                                className="rounded-none px-7 bg-brand-whatsapp hover:bg-brand-whatsapp/90 font-heading font-semibold h-12"
                            >
                                <a
                                    href={waLink(`Hi, I'm interested in ${course.short} in Pathankot`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                                </a>
                            </Button>
                        </div>
                    </div>
                    <div className="lg:col-span-5" id="course-enquiry">
                        <EnquiryForm
                            defaultCourse={course.short}
                            sourcePage={slug}
                        />
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section data-testid="course-benefits" className="section-y bg-white">
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
                        <div className="lg:col-span-7">
                            <div className="overline text-brand-crimson mb-3">
                                Course Benefits
                            </div>
                            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-navy tracking-tight leading-[1.05]">
                                What you get with Dacron's {course.short} programme.
                            </h2>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-black/10">
                        {course.benefits.map((b, i) => {
                            const Icon = benefitIcons[i % benefitIcons.length];
                            return (
                                <div
                                    key={i}
                                    data-testid={`benefit-${i}`}
                                    className="p-7 border-b lg:border-b-0 lg:border-r border-black/10 last:border-r-0 [&:nth-child(3n)]:lg:border-r-0 [&:nth-last-child(-n+3)]:lg:border-b-0 hover:bg-brand-surface transition-colors"
                                >
                                    <Icon className="w-8 h-8 text-brand-crimson mb-5" strokeWidth={1.5} />
                                    <p className="text-brand-ink leading-relaxed">{b}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* EXAM INFO */}
            <section data-testid="course-exam-info" className="section-y bg-brand-surface">
                <div className="container-x grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="overline text-brand-crimson mb-3">
                            Exam / Course Details
                        </div>
                        <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-navy tracking-tight leading-[1.05] mb-6">
                            Everything you need to know.
                        </h2>
                        <p className="text-brand-sub leading-relaxed mb-8">
                            Our counsellors share the latest selection process, eligibility
                            and exam pattern with every parent so you know exactly what
                            we're preparing your child for.
                        </p>
                        <Button
                            asChild
                            data-testid="exam-info-cta"
                            className="rounded-none h-12 px-7 bg-brand-navy hover:bg-brand-navyHover font-heading font-semibold"
                        >
                            <a href="#course-enquiry">Get Detailed Brochure</a>
                        </Button>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="bg-white border border-black/10">
                            {course.examInfo.map((row, i) => (
                                <div
                                    key={i}
                                    data-testid={`exam-row-${i}`}
                                    className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-black/10 last:border-b-0"
                                >
                                    <div className="col-span-4 overline text-brand-sub">
                                        {row.label}
                                    </div>
                                    <div className="col-span-8 font-heading font-semibold text-brand-navy">
                                        {row.value}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST BUILDING / WHY US LITE */}
            <section className="section-y bg-white">
                <div className="container-x">
                    <div className="grid md:grid-cols-3 gap-0 border border-black/10">
                        <div className="p-8 border-b md:border-b-0 md:border-r border-black/10">
                            <div className="font-heading font-black text-5xl text-brand-crimson mb-2">
                                100+
                            </div>
                            <div className="font-heading font-semibold text-brand-navy mb-2">
                                Defence Selections
                            </div>
                            <p className="text-sm text-brand-sub leading-relaxed">
                                Sainik, NDA, RIMC, RMS — selections every year since 2014.
                            </p>
                        </div>
                        <div className="p-8 border-b md:border-b-0 md:border-r border-black/10">
                            <div className="font-heading font-black text-5xl text-brand-crimson mb-2">
                                4.9★
                            </div>
                            <div className="font-heading font-semibold text-brand-navy mb-2">
                                Google Rating
                            </div>
                            <p className="text-sm text-brand-sub leading-relaxed">
                                287+ reviews from parents across Pathankot & Gurdaspur.
                            </p>
                        </div>
                        <div className="p-8">
                            <div className="font-heading font-black text-5xl text-brand-crimson mb-2">
                                20
                            </div>
                            <div className="font-heading font-semibold text-brand-navy mb-2">
                                Avg Batch Size
                            </div>
                            <p className="text-sm text-brand-sub leading-relaxed">
                                Personal attention. Direct mentor access. No overcrowding.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section data-testid="course-faq" className="section-y bg-brand-surface" id="faq">
                <div className="container-x grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="overline text-brand-crimson mb-3">FAQs</div>
                        <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-navy tracking-tight leading-[1.05] mb-6">
                            Parent questions about {course.short}, answered.
                        </h2>
                        <p className="text-brand-sub leading-relaxed mb-8">
                            Still unsure? Our counsellor will personally walk you through
                            the syllabus, fee structure and demo class.
                        </p>
                        <div className="flex gap-3">
                            <Button
                                asChild
                                className="rounded-none h-12 px-6 bg-brand-crimson hover:bg-brand-crimsonHover font-heading font-semibold"
                            >
                                <a href="#course-enquiry">Talk to Counsellor</a>
                            </Button>
                            <Button
                                asChild
                                variant="outline"
                                className="rounded-none h-12 px-6 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-heading font-semibold"
                            >
                                <a href={telLink}>
                                    <Phone className="w-4 h-4 mr-2" /> Call
                                </a>
                            </Button>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <Accordion type="single" collapsible className="border-t border-black/10 bg-white">
                            {course.faqs.map((f, i) => (
                                <AccordionItem
                                    key={i}
                                    value={`f-${i}`}
                                    data-testid={`course-faq-${i}`}
                                    className="border-b border-black/10 px-5"
                                >
                                    <AccordionTrigger className="text-left font-heading font-semibold text-brand-navy text-base md:text-lg py-5 hover:no-underline">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-brand-sub leading-relaxed pb-5">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* RELATED COURSES */}
            <section data-testid="related-courses" className="section-y bg-white">
                <div className="container-x">
                    <div className="overline text-brand-crimson mb-3">
                        Explore Related Programmes
                    </div>
                    <h2 className="font-heading font-black text-2xl md:text-3xl text-brand-navy mb-10">
                        Students who chose {course.short} also explored:
                    </h2>
                    <div className="grid md:grid-cols-3 gap-5">
                        {course.related.map((rslug) => {
                            const r = COURSE_BY_SLUG[rslug];
                            if (!r) return null;
                            return (
                                <Link
                                    to={`/${r.slug}`}
                                    key={r.slug}
                                    data-testid={`related-${r.slug}`}
                                    className="group bg-white border border-black/10 p-7 hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(10,25,47,0.25)] transition-all"
                                >
                                    <div className="flex items-start justify-between gap-3 mb-4">
                                        <div className="overline text-brand-crimson">
                                            {r.category}
                                        </div>
                                        <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                    <h3 className="font-heading font-extrabold text-xl text-brand-navy leading-tight">
                                        {r.short} <span className="text-brand-sub font-medium">in Pathankot</span>
                                    </h3>
                                </Link>
                            );
                        })}
                    </div>

                    <div className="mt-12 text-center">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 font-heading font-semibold text-brand-navy hover:text-brand-crimson"
                        >
                            <ChevronLeft className="w-4 h-4" /> Back to all courses
                        </Link>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="bg-brand-navy text-white">
                <div className="container-x py-14 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <div className="overline text-brand-gold mb-3">
                            Ready to Begin?
                        </div>
                        <h3 className="font-heading font-black text-3xl md:text-4xl tracking-tight leading-[1.1] max-w-lg">
                            Join Pathankot's most trusted {course.short.toLowerCase()} programme.
                        </h3>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                        <Button
                            asChild
                            className="rounded-none h-13 px-7 bg-brand-crimson hover:bg-brand-crimsonHover font-heading font-bold h-12"
                        >
                            <a href="#course-enquiry">Book Free Demo</a>
                        </Button>
                        <Button
                            asChild
                            variant="outline"
                            className="rounded-none px-7 border-white text-white hover:bg-white hover:text-brand-navy font-heading font-semibold bg-transparent h-12"
                        >
                            <a href={waLink(`Hi, I want details on ${course.short}`)} target="_blank" rel="noopener noreferrer">
                                WhatsApp Now
                            </a>
                        </Button>
                    </div>
                </div>
            </section>
        </main>
    );
}
